if (!window.DrawerBase) {
  window.DrawerBase = class DrawerBase extends HTMLElement {
    // ── State ──
    #isOpen = false;
    #overlay = null;
    #activeOverlay = null;
    #boundHandlers = {};

    get isOpen() {
      return this.#isOpen;
    }

    // ── Subclass MUST override ──
    get container() {
      throw new Error('DrawerBase: subclass must override get container()');
    }
    get trigger() {
      throw new Error('DrawerBase: subclass must override get trigger()');
    }
    get closeButton() {
      throw new Error('DrawerBase: subclass must override get closeButton()');
    }

    // ── Subclass MAY override ──
    get containerOpenClass() {
      return 'open';
    }
    get bodyOverlayClasses() {
      return ['drawer-overlay--active'];
    }
    get overlay() {
      if (!this.#overlay) {
        const cls = this.overlayClass;
        this.#overlay = this.querySelector('.' + cls);
        if (!this.#overlay) {
          this.#overlay = document.createElement('div');
          this.#overlay.classList.add(cls);
          this.#overlay.classList.add('hidden');
          this.appendChild(this.#overlay);
        }
      }
      return this.#overlay;
    }
    get overlayClass() {
      return 'page-overlay';
    }
    get focusableSelector() {
      return 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';
    }
    get tabindexTargets() {
      return null;
    }
    onOpen() {}
    onClose() {}
    shouldCloseOnClick(event) {
      const inside = this.container?.contains(event.target);
      const onTrigger = this.trigger?.contains(event.target);
      return !inside && !onTrigger;
    }

    // ── Core mechanics ──
    openDrawer() {
      const container = this.container;
      const overlay = this.overlay;
      if (!container) return;

      container.classList.add(this.containerOpenClass);
      if (overlay) overlay.classList.remove('hidden');
      this.bodyOverlayClasses.forEach(cls => document.body.classList.add(cls));
      this.#isOpen = true;
      this.#activeOverlay = overlay;
      this._updateTabindex();
      this.onOpen();

      document.addEventListener('click', this.#boundHandlers.clickOutside);
      if (overlay) overlay.addEventListener('click', this.#boundHandlers.close);
    }

    closeDrawer() {
      if (!this.#isOpen) return;
      const container = this.container;
      if (!container) return;

      container.classList.remove(this.containerOpenClass);
      if (this.#activeOverlay) this.#activeOverlay.classList.add('hidden');
      this.bodyOverlayClasses.forEach(cls => document.body.classList.remove(cls));
      this.#isOpen = false;
      this._updateTabindex();
      this.onClose();

      document.removeEventListener('click', this.#boundHandlers.clickOutside);
      if (this.#activeOverlay) {
        this.#activeOverlay.removeEventListener('click', this.#boundHandlers.close);
        this.#activeOverlay = null;
      }
    }

    // ── Lifecycle ──
    connectedCallback() {
      this._bindHandlers();
      this._updateTabindex();
      this._addPermanentListeners();
      this.onConnected();
    }

    disconnectedCallback() {
      this._removePermanentListeners();
      document.removeEventListener('click', this.#boundHandlers.clickOutside);
      // Use #activeOverlay — the actual element we bound listeners to.
      // Do NOT call this.overlay getter, which would auto-create during teardown.
      if (this.#activeOverlay) {
        this.#activeOverlay.removeEventListener('click', this.#boundHandlers.close);
        this.#activeOverlay = null;
      }
    }

    onConnected() {}

    // ── Internal ──
    _bindHandlers() {
      this.#boundHandlers = {
        triggerClick: (e) => {
          e.stopPropagation();
          this.openDrawer();
        },
        close: () => this.closeDrawer(),
        keyDown: (e) => this._onKeyDown(e),
        clickOutside: (e) => {
          if (this.#isOpen && this.shouldCloseOnClick(e)) {
            this.closeDrawer();
          }
        },
      };
    }

    _addPermanentListeners() {
      const trigger = this.trigger;
      const closeBtn = this.closeButton;
      const container = this.container;
      if (trigger) trigger.addEventListener('click', this.#boundHandlers.triggerClick);
      if (closeBtn) closeBtn.addEventListener('click', this.#boundHandlers.close);
      if (container) container.addEventListener('keydown', this.#boundHandlers.keyDown);
    }

    _removePermanentListeners() {
      const trigger = this.trigger;
      const closeBtn = this.closeButton;
      const container = this.container;
      if (trigger) trigger.removeEventListener('click', this.#boundHandlers.triggerClick);
      if (closeBtn) closeBtn.removeEventListener('click', this.#boundHandlers.close);
      if (container) container.removeEventListener('keydown', this.#boundHandlers.keyDown);
    }

    _onKeyDown(e) {
      if (e.key === 'Escape' && this.#isOpen) {
        this.closeDrawer();
        return;
      }
      if (e.key === 'Tab' && this.#isOpen) {
        DrawerBase.trapFocus(this.container, e, this.focusableSelector);
      }
    }

    _updateTabindex() {
      const selector = this.tabindexTargets;
      if (!selector || !this.container) return;
      const elements = this.container.querySelectorAll(selector);
      setTabindex(elements, this.#isOpen ? '0' : '-1');
    }

    // ── Static helpers (for non-CE consumers) ──
    static open(container, overlay, bodyClasses, openClass) {
      if (container) container.classList.add(openClass || 'open');
      if (overlay) overlay.classList.remove('hidden');
      (bodyClasses || []).forEach(cls => document.body.classList.add(cls));
    }

    static close(container, overlay, bodyClasses, openClass) {
      if (container) container.classList.remove(openClass || 'open');
      if (overlay) overlay.classList.add('hidden');
      (bodyClasses || []).forEach(cls => document.body.classList.remove(cls));
    }

    static trapFocus(container, event, selector) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        container.querySelectorAll(selector || 'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')
      ).filter(el => el.tabIndex >= 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    }
  };
}
